import React, { useEffect, useState } from 'react';
import { X, Share, PlusSquare, Download } from 'lucide-react';

const DISMISS_KEY = 'kca_install_prompt_dismissed';

function isIos(): boolean {
    return /iphone|ipad|ipod/i.test(navigator.userAgent) && !('MSStream' in window);
}

function isStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
}

/**
 * Prompts a first-time visitor to install the app to their home screen.
 *
 * Android/Chrome/Edge expose a real `beforeinstallprompt` event we can drive ourselves.
 * iOS Safari has no equivalent API at all — Apple doesn't let a web page trigger an
 * install prompt there, full stop — so the only honest option is to show the manual
 * "tap Share, then Add to Home Screen" steps instead of pretending a one-tap install exists.
 */
export const InstallPrompt: React.FC = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

        if (isIos()) {
            setPlatform('ios');
            setVisible(true);
            return;
        }

        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setPlatform('android');
            setVisible(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, '1');
        setVisible(false);
    };

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        dismiss();
    };

    // never over a page that is streamed or used as a camera (the Musabaqa broadcast and side camera)
    if (!visible || !platform || /^\/musabaqa\/(broadcast|sidecam)/.test(window.location.pathname)) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[90] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex items-start gap-3 animate-fade-in"
        >
            <div
                className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{ background: 'var(--brand-primary, #1B3870)' }}
            >
                <Download className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">Install this app</p>
                {platform === 'ios' ? (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        Tap <Share className="inline w-4 h-4 -mt-0.5" /> in Safari, then{' '}
                        <span className="inline-flex items-center gap-1 font-medium">
                            <PlusSquare className="w-4 h-4" /> Add to Home Screen
                        </span>{' '}
                        — works offline once installed.
                    </p>
                ) : (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        Add it to your home screen for faster access and offline use.
                    </p>
                )}
                {platform === 'android' && (
                    <button
                        onClick={install}
                        className="mt-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-white"
                        style={{ background: 'var(--brand-primary, #1B3870)' }}
                    >
                        Install
                    </button>
                )}
            </div>
            <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};
